import type { ObservabilityClient } from "../../observability/types";
import type { EventBus } from "../events/eventBus";
import type { RequestRuntimeContext } from "../context/runtimeContext";

export type CommandResult<T = unknown> = {
  ok: boolean;
  value?: T;
  error?: string;
  rollback?: { supported: boolean; reason?: string };
};

export interface Command<TInput, TOutput> {
  name: string;
  validate(input: TInput): void;
  execute(
    input: TInput,
    context: RequestRuntimeContext,
  ): Promise<CommandResult<TOutput>>;
}

export class CommandBus {
  constructor(
    private readonly eventBus: EventBus,
    private readonly observability: ObservabilityClient,
  ) {}

  async execute<TInput, TOutput>(
    command: Command<TInput, TOutput>,
    input: TInput,
    context: RequestRuntimeContext,
  ): Promise<CommandResult<TOutput>> {
    try {
      command.validate(input);
      const result = await command.execute(input, context);
      await this.eventBus.publish({
        id: `${command.name}-${Date.now()}`,
        name: result.ok ? "CommandExecuted" : "CommandFailed",
        payload: { command: command.name, result },
        timestamp: new Date().toISOString(),
        correlationId: context.correlationId,
      });
      await this.observability.event({
        eventName: "command_executed",
        message: `Command ${command.name} completed.`,
        category: "command",
        correlationId: context.correlationId,
        context: { ok: result.ok },
      });
      return result;
    } catch (error) {
      await this.observability.error({
        eventName: "command_failed",
        message: `Command ${command.name} failed.`,
        category: "command",
        correlationId: context.correlationId,
        error,
      });
      return {
        ok: false,
        error: error instanceof Error ? error.message : "Command failed.",
        rollback: { supported: false },
      };
    }
  }
}
